import { createTrailConverter, wgs84ToGcj02, type Wgs84Path } from './geo';
import { isAmapConfigured, loadAMap } from './amapLoader';

/**
 * 地图边界层：底图为高德 JSAPI 2.0。
 *
 * 这里同时承担两件必须成对出现的事：
 *
 * 1. 屏蔽 AMap 的 API 细节 —— 组件只跟本文件导出的 handle 打交道；
 * 2. **所有 WGS84 → GCJ-02 转换都发生在本文件内部** —— 组件永远只传 WGS84。
 *
 * 第 2 点是刻意的：高德底图是 GCJ-02，境内与 WGS84 相差数百米，
 * 只要有一处调用忘了转换，标记与航迹就会与道路错位，而且很难肉眼定位。
 * 把转换收口在唯一一层里，组件即使想漏也漏不掉。
 * 反过来，**界面其它地方（遥测完整数据表、注册信息）继续展示 WGS84 原始值**，
 * 那才是无人机的真实坐标，不要在这里之外做转换。
 */

/**
 * 高德官方内置样式：浅色用 normal，深色用 dark，深浅色切换靠 setMapStyle 而不是滤镜压暗。
 *
 * 深色特意不用 darkblue —— 实测该样式对本项目的 Key 会渲染成近乎纯黑的空画布
 * （内部区域只有 25 个颜色桶、无路网与标注），whitesmoke 同样不可用；
 * dark 实测 524 个颜色桶、单一色占比仅 47%，内容最完整且整体亮度 67，与深色界面匹配。
 */
const AMAP_STYLE = {
  light: 'amap://styles/normal',
  dark: 'amap://styles/dark',
} as const;

const TRACK_COLOR = '#4d9bff';
const SINGLE_POINT_ZOOM = 14;
/** 全览时不允许把地图推到比这更近，否则单机/小范围时视角会贴到贴图上 */
const MAX_FIT_ZOOM = 16;
/** 与旧实现 fitBounds 的 padding 语义对应；高德 setBounds 没有 padding 参数，按视口比例外扩 */
const FIT_PADDING_PX = 64;

/** 默认视角中心（北京，WGS84；建图时再转 GCJ-02） */
export const DEFAULT_CENTER: readonly [number, number] = [116.3913, 39.9075];
export const DEFAULT_ZOOM = 11;

export interface MarkerAppearance {
  status: string;
  /** 机头朝向（正北为 0，顺时针），离线/未解锁时为 null */
  headingDeg: number | null;
  selected: boolean;
  label?: string;
}

export interface DroneMarkerHandle {
  /** 传入 WGS84 坐标 */
  setPosition(lng: number, lat: number): void;
  setAppearance(appearance: MarkerAppearance): void;
  onClick(handler: () => void): void;
  remove(): void;
}

export interface TrailHandle {
  /** 传入 WGS84 航迹；同一个数组原地追加时只做增量坐标转换 */
  setPath(path: Wgs84Path): void;
  remove(): void;
}

export interface HavorixMap {
  setDark(dark: boolean): void;
  /** 把若干 WGS84 点纳入视野 */
  fitBounds(points: readonly (readonly [number, number])[], paddingPx?: number): void;
  /** 把视角移到某个 WGS84 点（用于跟随） */
  centerOn(lng: number, lat: number, durationMs: number): void;
  getZoom(): number;
  onDragStart(handler: () => void): void;
  offDragStart(handler: () => void): void;
  /** 添加一台无人机的标记 */
  addMarker(appearance: MarkerAppearance, lng: number, lat: number): DroneMarkerHandle;
  /** 为一条航迹创建图层（光带 + 主线两层） */
  createTrail(): TrailHandle;
  destroy(): void;
}

export interface CreateMapOptions {
  center?: readonly [number, number];
  zoom?: number;
  dark: boolean;
  /** 单机管理的地图不需要旋转/倾斜，避免误操作把姿态参照系转乱 */
  lockRotation?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  flying: 'var(--accent)',
  idle: 'var(--ok)',
  offline: 'var(--fg-subtle)',
  fault: 'var(--critical)',
  disabled: 'var(--fg-subtle)',
};

let warnedAboutMap = false;
function warnOnce(message: string): void {
  if (warnedAboutMap) return;
  warnedAboutMap = true;
  console.warn(`[havorix] ${message}`);
}

function renderUnavailable(container: HTMLElement, message: string): void {
  container.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'havorix-map-unavailable';
  box.textContent = message;
  container.appendChild(box);
}

/**
 * 生成无人机标记 DOM。
 *
 * 用 DOM 标记而不是符号图层：一是不需要字体/图标资源，二是
 * 俯仰/朝向变化时只要改 `transform` 一个属性，开销足够小。
 */
function createDroneMarkerElement(appearance: MarkerAppearance): HTMLDivElement {
  const root = document.createElement('div');
  root.className = 'havorix-marker';
  root.style.width = '0';
  root.style.height = '0';
  root.innerHTML = `
    <div class="havorix-marker__inner" style="--marker-color:${STATUS_COLORS[appearance.status] ?? 'var(--accent)'}">
      <span class="havorix-marker__halo" data-role="halo" aria-hidden="true"></span>
      <div class="havorix-marker__rotor" data-role="heading">
        <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
          <path d="M12 2.5 21 20l-9-4.2L3 20 12 2.5Z" fill="var(--marker-color)" stroke="var(--surface-1)" stroke-width="1.2" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="havorix-marker__label" data-role="label"></span>
    </div>
  `;
  applyMarkerAppearance(root, appearance);
  return root;
}

function applyMarkerAppearance(root: HTMLDivElement, appearance: MarkerAppearance): void {
  const inner = root.querySelector<HTMLElement>('.havorix-marker__inner');
  if (!inner) return;
  inner.style.setProperty('--marker-color', STATUS_COLORS[appearance.status] ?? 'var(--accent)');
  inner.classList.toggle('is-selected', appearance.selected);
  const rotor = root.querySelector<HTMLElement>('[data-role="heading"]');
  if (rotor) {
    const heading = appearance.headingDeg ?? 0;
    rotor.style.transform = `rotate(${heading}deg)`;
    rotor.style.opacity = appearance.headingDeg === null ? '0.55' : '1';
  }
  const label = root.querySelector<HTMLElement>('[data-role="label"]');
  if (label && appearance.label !== undefined && label.textContent !== appearance.label) {
    label.textContent = appearance.label;
  }
}

/** 球面墨卡托的 y 分量（0~1），用于由纬度跨度反推缩放级别 */
function mercatorY(lat: number): number {
  const rad = (lat * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

/**
 * 创建高德地图。返回 `null` 表示地图不可用（缺 Key 或脚本加载失败），
 * 此时容器里已经放好了给运维看的提示文字，调用方直接跳过后续地图逻辑即可。
 */
export async function createBaseMap(
  container: HTMLElement,
  options: CreateMapOptions,
): Promise<HavorixMap | null> {
  if (!isAmapConfigured()) {
    renderUnavailable(container, '未配置高德地图 Key（VITE_AMAP_KEY），地图暂不可用。');
    warnOnce('未配置 VITE_AMAP_KEY，地图已禁用');
    return null;
  }

  let amap: typeof AMap;
  try {
    amap = await loadAMap();
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    renderUnavailable(container, `高德地图加载失败：${detail}`);
    warnOnce(`高德地图加载失败：${detail}`);
    return null;
  }

  const lockRotation = options.lockRotation === true;
  const [centerLng, centerLat] = options.center ?? DEFAULT_CENTER;
  const map = new amap.Map(container, {
    viewMode: '2D',
    center: wgs84ToGcj02(centerLng, centerLat),
    zoom: options.zoom ?? DEFAULT_ZOOM,
    resizeEnable: true,
    rotateEnable: !lockRotation,
    pitchEnable: !lockRotation,
    mapStyle: options.dark ? AMAP_STYLE.dark : AMAP_STYLE.light,
  });

  map.addControl(new amap.ToolBar({ position: { right: '12px', top: '12px' } }));
  map.addControl(new amap.Scale({ position: { left: '12px', bottom: '12px' } }));

  return {
    setDark(dark) {
      map.setMapStyle(dark ? AMAP_STYLE.dark : AMAP_STYLE.light);
    },

    fitBounds(points, paddingPx = FIT_PADDING_PX) {
      if (points.length === 0) return;

      if (points.length === 1) {
        const point = points[0]!;
        map.setZoomAndCenter(
          Math.max(map.getZoom(), SINGLE_POINT_ZOOM),
          wgs84ToGcj02(point[0], point[1]),
        );
        return;
      }

      let minLng = Infinity;
      let maxLng = -Infinity;
      let minLat = Infinity;
      let maxLat = -Infinity;
      for (const [lng, lat] of points) {
        const [gcjLng, gcjLat] = wgs84ToGcj02(lng, lat);
        minLng = Math.min(minLng, gcjLng);
        maxLng = Math.max(maxLng, gcjLng);
        minLat = Math.min(minLat, gcjLat);
        maxLat = Math.max(maxLat, gcjLat);
      }

      const container = map.getContainer();
      const width = container.clientWidth || 800;
      const height = container.clientHeight || 600;
      const lngSpan = maxLng - minLng;
      const latSpan = maxLat - minLat;

      // 高德 setBounds 没有 padding 参数，按视口比例把范围外扩，等价于预留一圈空白
      const padLng = (lngSpan * paddingPx) / width;
      const padLat = (latSpan * paddingPx) / height;

      // setBounds 不限制最大缩放，范围很小时会一路贴到贴图上，所以先估算缩放再决定
      const paddedLngSpan = lngSpan + padLng * 2;
      const paddedLatSpanMerc = Math.max(
        mercatorY(maxLat + padLat) - mercatorY(minLat - padLat),
        Number.EPSILON,
      );
      const zoomLng = Math.log2(width / 256 / (paddedLngSpan / 360));
      const zoomLat = Math.log2(height / 256 / paddedLatSpanMerc);

      if (Math.min(zoomLng, zoomLat) > MAX_FIT_ZOOM) {
        map.setZoomAndCenter(MAX_FIT_ZOOM, [
          (minLng + maxLng) / 2,
          (minLat + maxLat) / 2,
        ]);
        return;
      }

      map.setBounds(
        new amap.Bounds([minLng - padLng, minLat - padLat], [maxLng + padLng, maxLat + padLat]),
      );
    },

    centerOn(lng, lat, durationMs) {
      map.setCenter(wgs84ToGcj02(lng, lat), false, durationMs);
    },

    getZoom() {
      return map.getZoom();
    },

    onDragStart(handler) {
      map.on('dragstart', handler);
    },

    offDragStart(handler) {
      map.off('dragstart', handler);
    },

    addMarker(appearance, lng, lat) {
      const element = createDroneMarkerElement(appearance);
      const marker = new amap.Marker({
        position: wgs84ToGcj02(lng, lat),
        content: element,
        anchor: 'center',
        zIndex: 120,
      });
      map.add(marker);
      return {
        setPosition(nextLng, nextLat) {
          marker.setPosition(wgs84ToGcj02(nextLng, nextLat));
        },
        setAppearance(next) {
          applyMarkerAppearance(element, next);
        },
        onClick(handler) {
          element.addEventListener('click', (event) => {
            event.stopPropagation();
            handler();
          });
        },
        remove() {
          map.remove(marker);
        },
      };
    },

    createTrail() {
      const toGcj = createTrailConverter();
      // 先铺一层更宽更淡的蓝色光带，再叠实线：底图是彩色地图，
      // 单靠 2.5px 的细线在密集地物上很容易被淹没。
      const glow = new amap.Polyline({
        strokeColor: TRACK_COLOR,
        strokeWeight: 7,
        strokeOpacity: 0.28,
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 110,
      });
      const line = new amap.Polyline({
        strokeColor: TRACK_COLOR,
        strokeWeight: 2.5,
        strokeOpacity: 0.95,
        strokeStyle: 'dashed',
        strokeDasharray: [2, 1.5],
        lineJoin: 'round',
        lineCap: 'round',
        zIndex: 111,
      });
      map.add([glow, line]);

      return {
        setPath(path) {
          // 高德对空 path 的行为没有保证，落点不足两个时直接隐藏而不是画一条空线
          if (path.length < 2) {
            glow.hide();
            line.hide();
            return;
          }
          const points = toGcj(path);
          glow.setPath(points);
          line.setPath(points);
          glow.show();
          line.show();
        },
        remove() {
          map.remove([glow, line]);
        },
      };
    },

    destroy() {
      map.destroy();
    },
  };
}
